import React, { useEffect, useRef, useState } from "react";

// Healthie embedded intake form (form_only) — completing it provisions the
// member's unique Healthie ID inside the practice's Healthie account.
const HEALTHIE_INTAKE_URL =
  "https://secure.gethealthie.com/appointments/embed_appt?dietitian_id=14293593&embed_form_id=3166144&form_only=true&primary_color=11296b";

export function HealthieIntakeModal({ open, onClose, onSignIn }) {
  const closeRef = useRef(null);
  const [step, setStep] = useState("form"); // "form" | "done"

  useEffect(() => {
    if (!open) return undefined;
    setStep("form");
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center p-3 sm:p-6 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="healthie-intake-title"
      data-testid="healthie-intake-modal"
    >
      <div
        className="absolute inset-0"
        style={{ background: "rgba(10, 20, 40, 0.62)", backdropFilter: "blur(4px)" }}
        onClick={onClose}
        data-testid="healthie-intake-backdrop"
        aria-hidden="true"
      />

      <div
        className="relative w-full max-w-2xl flex flex-col bg-white shadow-2xl my-auto"
        style={{
          border: "1px solid var(--iw-border)",
          borderRadius: "3px",
          maxHeight: "94vh",
        }}
      >
        <div
          className="flex items-center justify-between px-5 sm:px-6 py-4 border-b flex-shrink-0"
          style={{ borderColor: "var(--iw-border)" }}
        >
          <div className="flex flex-col leading-tight">
            <span
              className="iw-mono uppercase"
              style={{
                color: "var(--iw-accent-red)",
                fontSize: "10px",
                letterSpacing: "0.22em",
                fontWeight: 600,
              }}
            >
              Get Started
            </span>
            <span
              id="healthie-intake-title"
              className="serif font-bold"
              style={{ color: "var(--iw-primary)", fontSize: "18px" }}
            >
              New member intake
            </span>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center w-9 h-9 transition-all"
            style={{
              border: "1px solid var(--iw-border)",
              borderRadius: "2px",
              color: "var(--iw-primary)",
            }}
            aria-label="Close intake form"
            data-testid="healthie-intake-close-btn"
          >
            <span aria-hidden="true" className="font-mono text-base leading-none">
              ✕
            </span>
          </button>
        </div>

        <div className="flex-1 overflow-hidden" style={{ background: "var(--iw-bg)" }}>
          {step === "form" ? (
            <iframe
              src={HEALTHIE_INTAKE_URL}
              title="New member intake form"
              className="w-full h-full"
              style={{ minHeight: "60vh", border: "none", display: "block" }}
              data-testid="healthie-intake-iframe"
              allow="clipboard-write"
            />
          ) : (
            <div
              className="flex flex-col items-center justify-center text-center px-6 py-12 h-full"
              style={{ minHeight: "50vh" }}
              data-testid="healthie-intake-confirmation"
            >
              <div
                className="flex items-center justify-center mb-5"
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: "var(--iw-primary)",
                  color: "#fff",
                  fontSize: 26,
                }}
                aria-hidden="true"
              >
                ✓
              </div>
              <div
                className="iw-mono uppercase mb-2"
                style={{ color: "var(--iw-accent-red)", fontSize: "10px", letterSpacing: "0.22em", fontWeight: 600 }}
              >
                You're all set
              </div>
              <h3 className="serif font-bold m-0 mb-3" style={{ color: "var(--iw-primary)", fontSize: "24px" }}>
                Welcome to your reset
              </h3>
              <p className="leading-relaxed max-w-md m-0" style={{ color: "var(--iw-body)", fontSize: "15px" }}>
                Thanks for completing your intake. Our wellness team will review it and email you your{" "}
                <strong>member ID</strong>. Once it arrives, sign in with your ID and email to start your
                first reset.
              </p>
              <div className="flex items-center gap-3 mt-7 flex-wrap justify-center">
                <button
                  type="button"
                  className="iw-btn-primary"
                  onClick={onSignIn}
                  data-testid="healthie-intake-signin-btn"
                >
                  Sign in <span aria-hidden="true">↗</span>
                </button>
                <button
                  type="button"
                  className="iw-btn-ghost"
                  style={{ padding: "0.65rem 1rem", fontSize: "12px" }}
                  onClick={onClose}
                  data-testid="healthie-intake-done-close-btn"
                >
                  Back to site
                </button>
              </div>
            </div>
          )}
        </div>

        <div
          className="px-5 sm:px-6 py-3 border-t flex-shrink-0 flex items-center justify-between gap-3 flex-wrap"
          style={{ borderColor: "var(--iw-border)" }}
        >
          <p
            className="leading-relaxed m-0"
            style={{ color: "var(--iw-muted)", fontSize: "12px" }}
            data-testid="healthie-intake-footnote"
          >
            Wellness intake · not a medical form. In crisis, call{" "}
            <a href="tel:988" style={{ color: "var(--iw-blue)", fontWeight: 600 }}>
              988
            </a>{" "}
            or text{" "}
            <a href="sms:741741" style={{ color: "var(--iw-blue)", fontWeight: 600 }}>
              741741
            </a>
            .
          </p>
          {step === "form" && (
            <button
              type="button"
              className="iw-btn-ghost flex-shrink-0"
              style={{ padding: "0.5rem 0.9rem", fontSize: "12px" }}
              onClick={() => setStep("done")}
              data-testid="healthie-intake-finished-btn"
            >
              I've finished my intake →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default HealthieIntakeModal;
